/* @moldura padrao */
/**
 * page.tsx (meus-dados) — A pessoa preenche a PRÓPRIA ficha de funcionário.
 *
 * Separada de /perfil de propósito, e a separação é a mesma do banco: "Meu perfil"
 * é a CREDENCIAL (usuário, senha); aqui é a PESSOA (documentos, PIX, endereço).
 *
 * O LAYOUT VEM DO IRMÃO `webapp-rm-ops` (aba "Pessoais" da ficha), portado em
 * 08/08/2026 depois que a primeira versão — cards empilhados, grade rígida, campos
 * crus — foi reprovada. Um cartão só, campos com ícone à esquerda e largura
 * conforme o conteúdo: nome completo grande, CPF pequeno, e-mail largo. Ver
 * [[pessoal-espelha-rm-ops]]: tudo de pessoal se copia de lá.
 *
 * Bloqueio: depois que a gestão confere e trava, a tela vira leitura. O servidor
 * recusa de qualquer jeito (403 no PUT /funcionarios/minha) — o `fieldset` daqui é
 * cortesia, não a trava.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { CustomCampoData } from '@/components/CustomCamposData';
import {
  CustomCampoCPF,
  CustomCampoPIS,
  CustomCampoTelefone,
  CustomCampoTexto,
} from '@/components/CustomCamposMascara';
import { api, ApiError } from '@/lib/api';
import { comprimirImagem } from '@/lib/imagem';
import { CustomMolduraPagina } from '@/components/CustomMolduraPagina';

interface Ficha {
  id: number;
  nome: string;
  apelido: string | null;
  cargo: string | null;
  cpf: string | null;
  rg: string | null;
  pis: string | null;
  nascimento: string | null;
  pix: string | null;
  pix_tipo: string | null;
  conta_salario: string | null;
  telefone: string | null;
  celular: string | null;
  email: string | null;
  endereco: string | null;
  bairro: string | null;
  cidade: string | null;
  foto: string | null;
  admissao: string | null;
  situacao: string;
  loja_nome: string | null;
  edicao_bloqueada: boolean;
}

interface UploadFoto {
  arquivo: string;
  url: string;
}

const TIPOS_PIX = [
  { valor: 'celular', rotulo: 'Celular' },
  { valor: 'cpf', rotulo: 'CPF' },
  { valor: 'email', rotulo: 'E-mail' },
  { valor: 'aleatoria', rotulo: 'Chave aleatória' },
];

export function MeusDadosPage() {
  const [ficha, setFicha] = useState<Ficha | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [enviandoFoto, setEnviandoFoto] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api
      .get<Ficha | null>('/funcionarios/me')
      .then(setFicha)
      .catch(() => toast.error('Não foi possível carregar seus dados.'))
      .finally(() => setCarregando(false));
  }, []);

  function campo<K extends keyof Ficha>(chave: K, valor: Ficha[K]) {
    setFicha((f) => (f ? { ...f, [chave]: valor } : f));
  }

  /** A foto sobe na hora, e só o caminho é gravado no Salvar. */
  async function onArquivo(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setEnviandoFoto(true);
    try {
      const comprimido = await comprimirImagem(file);
      const fd = new FormData();
      fd.append('arquivo', comprimido);
      const resp = await api.upload<UploadFoto>('/funcionarios/me/foto', fd);
      campo('foto', resp.url);
      await api.put('/funcionarios/me', { foto: resp.arquivo });
      toast.success('Foto atualizada.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Falha ao enviar a foto.');
    } finally {
      setEnviandoFoto(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function salvar() {
    if (!ficha) return;
    if (!ficha.nome.trim()) return toast.error('Informe o nome.');
    setSalvando(true);
    try {
      const atualizada = await api.put<Ficha>('/funcionarios/me', {
        nome: ficha.nome.trim(),
        apelido: ficha.apelido || null,
        cpf: ficha.cpf || null,
        rg: ficha.rg || null,
        pis: ficha.pis || null,
        nascimento: ficha.nascimento || null,
        pix: ficha.pix || null,
        pix_tipo: ficha.pix_tipo || null,
        conta_salario: ficha.conta_salario || null,
        telefone: ficha.telefone || null,
        celular: ficha.celular || null,
        email: ficha.email || null,
        endereco: ficha.endereco || null,
        bairro: ficha.bairro || null,
        cidade: ficha.cidade || null,
      });
      setFicha(atualizada);
      toast.success('Dados salvos.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  if (carregando) {
    return (
      <div className="container py-4 text-center text-secondary">
        <span className="spinner-border spinner-border-sm me-2" />
        Carregando…
      </div>
    );
  }

  // Login sem ficha vinculada: dizer o que é, em vez de formulário vazio que não salva.
  if (!ficha) {
    return (
      <div className="container py-3">
        <div className="alert alert-warning mt-4">
          <i className="bi bi-exclamation-triangle me-2"></i>
          Seu acesso ainda não está vinculado a uma ficha de funcionário. Peça à
          gestão para criar a sua — os dados aparecem aqui depois disso.
        </div>
      </div>
    );
  }

  const travada = ficha.edicao_bloqueada;

  return (
    // `container`, não `container-fluid`: é o que a ficha do irmão usa
    // (`funcionarios/form.tsx`). O fluid ocupa 100% da largura e cola o cartão
    // nas bordas da tela; o container tem largura máxima por breakpoint e deixa
    // a margem lateral que o Vittorio sentiu falta (08/08/2026). O `py-3` é o
    // respiro de cima e de baixo, também de lá.
    /* As ações ficam à direita do título, como na ficha do irmão: quem rola até
       o fim não precisa voltar ao topo, e quem chega não precisa procurar. */
    <CustomMolduraPagina
      titulo="Meus dados"
      descricao="Dados pessoais, documentos e forma de recebimento"
      acoes={
        <>
          <Link to="/pessoal/acesso" className="btn btn-sm btn-outline-theme">
            <i className="bi bi-shield-lock me-1"></i>Acesso e senha
          </Link>
          {!travada && (
            <button className="btn btn-theme" onClick={salvar} disabled={salvando}>
              {salvando && <span className="spinner-border spinner-border-sm me-1" />}
              Salvar
            </button>
          )}
        </>
      }
    >

      {travada && (
        <div className="alert alert-info d-flex align-items-center" role="status">
          <i className="bi bi-lock me-2"></i>
          <div className="small">
            Sua ficha já foi conferida e está <strong>bloqueada para edição</strong>.
            Se algum dado mudou, fale com a gestão.
          </div>
        </div>
      )}

      <div className="card adminuiux-card">
        <div className="card-body">
          {/* Foto: inline e discreta, como lá — não é o assunto da tela. */}
          <div className="row gx-3 align-items-center mb-4">
            <div className="col-auto">
              {ficha.foto ? (
                <figure className="avatar avatar-70 rounded-circle coverimg mb-0">
                  <img src={ficha.foto} alt="" />
                </figure>
              ) : (
                <span className="avatar avatar-70 rounded-circle bg-theme-1 bg-opacity-10 text-theme-1 d-flex align-items-center justify-content-center">
                  <i className="bi bi-person h4 mb-0"></i>
                </span>
              )}
            </div>
            <div className="col-auto">
              <button
                className="btn btn-sm btn-link text-theme-1 px-0"
                onClick={() => fileRef.current?.click()}
                disabled={enviandoFoto || travada}
              >
                {enviandoFoto ? (
                  <span className="spinner-border spinner-border-sm me-1" />
                ) : (
                  <i className="bi bi-camera me-1"></i>
                )}
                Trocar foto
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                className="d-none"
                onChange={onArquivo}
              />
            </div>
            <div className="col text-end">
              <span className="badge bg-theme-1-subtle text-theme-1">
                {ficha.cargo ?? 'Sem cargo'}
              </span>
              <div className="text-secondary small mt-1">{ficha.loja_nome ?? '—'}</div>
            </div>
          </div>

          {/* Um fieldset só: cobre os componentes que não expõem `disabled`
              (CustomCampoData) e faz um campo novo nascer travado por estar aqui dentro. */}
          <fieldset disabled={travada} className="border-0 p-0 m-0">
            <div className="row g-3">
              <div className="col-12 col-md-8">
                <CustomCampoTexto
                  id="fic-nome"
                  label="Nome completo"
                  valor={ficha.nome}
                  onChange={(v) => campo('nome', v)}
                  ajuda="Como está no documento."
                />
              </div>
              <div className="col-12 col-md-4">
                <CustomCampoTexto
                  id="fic-apelido"
                  label="Nome curto"
                  valor={ficha.apelido ?? ''}
                  onChange={(v) => campo('apelido', v)}
                  ajuda="É como você aparece nas listas."
                />
              </div>

              <div className="col-12 col-md-4">
                <CustomCampoCPF
                  id="fic-cpf"
                  label="CPF"
                  valor={ficha.cpf ?? ''}
                  onChange={(v) => campo('cpf', v)}
                />
              </div>
              <div className="col-12 col-md-4">
                <CustomCampoData
                  id="fic-nascimento"
                  label="Nascimento"
                  valor={ficha.nascimento ?? ''}
                  onChange={(iso) => campo('nascimento', iso || null)}
                />
              </div>
              <div className="col-12 col-md-4">
                <CustomCampoTelefone
                  id="fic-celular"
                  label="Celular / WhatsApp"
                  icone="bi-whatsapp"
                  corIcone="text-success"
                  valor={ficha.celular ?? ''}
                  onChange={(v) => campo('celular', v)}
                />
              </div>

              <div className="col-12 col-md-4">
                <CustomCampoTelefone
                  id="fic-telefone"
                  label="Telefone fixo"
                  icone="bi-telephone"
                  valor={ficha.telefone ?? ''}
                  onChange={(v) => campo('telefone', v)}
                />
              </div>
              <div className="col-12 col-md-8">
                <CustomCampoTexto
                  id="fic-email"
                  label="E-mail"
                  type="email"
                  inputMode="email"
                  icone="bi-envelope"
                  valor={ficha.email ?? ''}
                  onChange={(v) => campo('email', v)}
                />
              </div>

              <div className="col-12 col-md-6">
                <CustomCampoTexto
                  id="fic-rg"
                  label="RG"
                  maxLength={20}
                  valor={ficha.rg ?? ''}
                  onChange={(v) => campo('rg', v)}
                />
              </div>
              <div className="col-12 col-md-6">
                <CustomCampoPIS
                  id="fic-pis"
                  label="PIS"
                  valor={ficha.pis ?? ''}
                  onChange={(v) => campo('pis', v)}
                />
              </div>

              <div className="col-12 col-md-4">
                <div className="form-floating">
                  <select
                    id="fic-pix-tipo"
                    className="form-select"
                    value={ficha.pix_tipo ?? ''}
                    onChange={(e) => campo('pix_tipo', e.target.value || null)}
                  >
                    <option value="">—</option>
                    {TIPOS_PIX.map((t) => (
                      <option key={t.valor} value={t.valor}>
                        {t.rotulo}
                      </option>
                    ))}
                  </select>
                  <label htmlFor="fic-pix-tipo">Tipo da chave PIX</label>
                </div>
              </div>
              <div className="col-12 col-md-8">
                <CustomCampoTexto
                  id="fic-pix"
                  label="Chave PIX"
                  icone="bi-key"
                  valor={ficha.pix ?? ''}
                  onChange={(v) => campo('pix', v)}
                  ajuda="Para receber reembolso sem depender da conta salário."
                />
              </div>

              <div className="col-12">
                <CustomCampoTexto
                  id="fic-endereco"
                  label="Endereço"
                  icone="bi-geo-alt"
                  valor={ficha.endereco ?? ''}
                  onChange={(v) => campo('endereco', v)}
                />
              </div>
              <div className="col-12 col-md-6">
                <CustomCampoTexto
                  id="fic-bairro"
                  label="Bairro"
                  valor={ficha.bairro ?? ''}
                  onChange={(v) => campo('bairro', v)}
                />
              </div>
              <div className="col-12 col-md-6">
                <CustomCampoTexto
                  id="fic-cidade"
                  label="Cidade"
                  valor={ficha.cidade ?? ''}
                  onChange={(v) => campo('cidade', v)}
                />
              </div>

              <div className="col-12 col-md-6">
                <CustomCampoTexto
                  id="fic-conta"
                  label="Conta salário"
                  icone="bi-bank"
                  valor={ficha.conta_salario ?? ''}
                  onChange={(v) => campo('conta_salario', v)}
                  ajuda="Agência e conta, se já tiver."
                />
              </div>
            </div>
          </fieldset>
        </div>

        {!travada && (
          <div className="card-footer bg-transparent text-end">
            <button className="btn btn-theme" onClick={salvar} disabled={salvando}>
              {salvando && <span className="spinner-border spinner-border-sm me-1" />}
              Salvar meus dados
            </button>
          </div>
        )}
      </div>

      <p className="text-secondary small mt-3">
        <i className="bi bi-info-circle me-1"></i>
        Cargo, loja, admissão e situação são preenchidos pela gestão.
      </p>
    </CustomMolduraPagina>
  );
}
